import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ActiveRentalCard from './components/ActiveRentalCard';
import FavoriteTrailerCard from './components/FavoriteTrailerCard';
import QuickSearchWidget from './components/QuickSearchWidget';
import RentalHistoryCard from './components/RentalHistoryCard';
import MfaSetupModal from './components/MfaSetupModal';
import { mfaService } from '../../services/mfaService';
import { supabase } from '../../lib/supabase';
import DashboardSkeleton from '../../components/ui/DashboardSkeleton';
import ErrorFallback from '../../components/ui/ErrorFallback';
import CardSkeleton from '../../components/ui/CardSkeleton';
import { bookingService } from '../../services/bookingService';
import { fetchFavoriteTrailers } from '../../services/trailerListingService';
import returnInspectionService from '../../services/returnInspectionService';

const RenterDashboard = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [activeRentals, setActiveRentals] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  // Real-time subscription for renter bookings
  useEffect(() => {
    if (!user?.id) return;

    const bookingsChannel = supabase
      ?.channel('renter_bookings_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `renter_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Add new booking to active rentals
            loadDashboardData();
          } else if (payload?.eventType === 'UPDATE') {
            // Update existing booking
            setActiveRentals((prev) =>
              prev?.map((rental) =>
                rental?.id === payload?.new?.id ? { ...rental, ...payload?.new } : rental
              )
            );
            setRentalHistory((prev) =>
              prev?.map((rental) =>
                rental?.id === payload?.new?.id ? { ...rental, ...payload?.new } : rental
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            // Remove deleted booking
            setActiveRentals((prev) => prev?.filter((r) => r?.id !== payload?.old?.id));
            setRentalHistory((prev) => prev?.filter((r) => r?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(bookingsChannel);
    };
  }, [user?.id]);

  const loadDashboardData = async () => {
    if (!user?.id) return;

    setIsLoadingData(true);
    setError(null);

    try {
      // Fetch active rentals
      const activeRentalsData = await bookingService?.fetchActiveRentals(user?.id);
      const transformedActiveRentals = activeRentalsData?.map(booking => ({
        id: booking?.id,
        trailerName: booking?.trailer?.title || 'Unknown Trailer',
        trailerImage: booking?.trailer?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png',
        trailerImageAlt: `${booking?.trailer?.title} - ${booking?.trailer?.trailerType}`,
        location: `${booking?.trailer?.locationCity}, ${booking?.trailer?.locationState}`,
        pickupDate: booking?.startDate,
        returnDate: booking?.endDate,
        pickupTime: booking?.pickupTime || null,
        dropoffTime: booking?.dropoffTime || null,
        status: booking?.status,
        ownerName: booking?.owner?.fullName || 'Owner',
        ownerPhone: booking?.owner?.phone || '',
        ownerId: booking?.ownerId || booking?.owner?.id || '',
        trailerId: booking?.trailer?.id || '',
        trailerListingId: booking?.trailerListingId || booking?.trailer?.id || '',
        totalCost: parseFloat(booking?.totalAmount || 0)?.toFixed(2)
      }));
      setActiveRentals(transformedActiveRentals || []);

      // Fetch rental history
      const rentalHistoryData = await bookingService?.fetchRentalHistory(user?.id);

      // Check which completed bookings already have return inspections
      const rentalHistoryWithInspectionStatus = await Promise.all(
        (rentalHistoryData || [])?.map(async (booking) => {
          try {
            const { data: inspection } = await returnInspectionService?.getInspectionByBookingId(booking?.id);
            return { ...booking, returnInspectionDone: !!inspection };
          } catch {
            return { ...booking, returnInspectionDone: false };
          }
        })
      );

      const transformedRentalHistory = rentalHistoryWithInspectionStatus?.map(booking => ({
        id: booking?.id,
        trailerName: booking?.trailer?.title || 'Unknown Trailer',
        trailerType: booking?.trailer?.trailerType || 'Unknown',
        trailerImage: booking?.trailer?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png',
        trailerImageAlt: `${booking?.trailer?.title} - ${booking?.trailer?.trailerType}`,
        location: `${booking?.trailer?.locationCity}, ${booking?.trailer?.locationState}`,
        rentalDate: booking?.startDate,
        returnDate: booking?.endDate,
        ownerId: booking?.ownerId || booking?.owner?.id || '',
        trailerId: booking?.trailer?.id || '',
        trailerListingId: booking?.trailerListingId || booking?.trailer?.id || '',
        totalCost: parseFloat(booking?.totalAmount || 0)?.toFixed(2),
        returnInspectionDone: booking?.returnInspectionDone || false,
        rated: false // TODO: Check if rating exists
      }));
      setRentalHistory(transformedRentalHistory || []);

      // Fetch favorite trailers
      const favoritesData = await fetchFavoriteTrailers(user?.id);
      setFavorites(favoritesData || []);

      // TODO: Fetch notifications
      setNotifications([]);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const loadMfaStatus = async () => {
    const { data } = await mfaService?.getMfaStatus(user?.id);
    if (data) {
      setMfaEnabled(data?.mfaEnabled);
      if (data?.mfaEnabled) {
        const { data: count } = await mfaService?.getBackupCodesCount(user?.id);
        setBackupCodesCount(count);
      }
    }
  };

  const handleMfaSetupSuccess = () => {
    loadMfaStatus();
  };

  useEffect(() => {
    // Active rentals will be loaded from real-time subscriptions
    setActiveRentals([]);

    // Favorites will be loaded from real-time subscriptions
    setFavorites([]);

    // Rental history will be loaded from real-time subscriptions
    setRentalHistory([]);

    // Notifications will be loaded from real-time subscriptions
    setNotifications([]);
  }, []);

  if (loading || isLoadingData) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <DashboardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ErrorFallback 
              error={error} 
              onRetry={loadDashboardData} 
              title="Failed to load dashboard data"
            />
          </div>
        </div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date()?.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const unreadNotifications = notifications?.filter((n) => !n?.read)?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              {getGreeting()}, {userProfile?.full_name?.split(' ')?.[0] || 'there'}!
            </h1>
            <p className="font-body text-muted-foreground">
              Welcome to your renter dashboard. Manage your rentals and discover new trailers.
            </p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Button
              variant="default"
              size="lg"
              iconName="Search"
              onClick={() => navigate('/search-results')}
              className="w-full">
              
              Browse Trailers
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Heart"
              onClick={() => document.getElementById('favorites')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full">
              
              View Favorites
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Bell"
              onClick={() => document.getElementById('notifications')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full relative">
              
              Notifications
              {unreadNotifications > 0 &&
              <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {unreadNotifications}
                </span>
              }
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Shield"
              onClick={() => setShowMfaModal(true)}
              className="w-full">
              
              {mfaEnabled ? 'Manage 2FA' : 'Enable 2FA'}
            </Button>
          </div>

          {/* Quick Search Widget */}
          <div className="mb-8">
            <QuickSearchWidget />
          </div>

          {/* Active Rentals Section */}
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Calendar" size={24} strokeWidth={2} className="text-primary" />
                <span>Active Rentals</span>
              </h2>
              {activeRentals?.length > 0 &&
              <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  {activeRentals?.length} active
                </span>
              }
            </div>
            {isLoadingData ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {[1, 2]?.map((i) => (
                  <CardSkeleton key={i} variant="horizontal" />
                ))}
              </div>
            ) : activeRentals?.length > 0 ?
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {activeRentals?.map((rental) =>
              <ActiveRentalCard key={rental?.id} rental={rental} />
              )}
              </div> :

            <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
                <Icon name="Calendar" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
                <p className="font-body text-muted-foreground mb-4">You don't have any active rentals</p>
                <Button variant="default" onClick={() => navigate('/search-results')}>
                  Browse Trailers
                </Button>
              </div>
            }
          </section>

          {/* Favorites Section */}
          <section id="favorites" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Heart" size={24} strokeWidth={2} className="text-primary" />
                <span>Favorite Trailers</span>
              </h2>
            </div>
            {isLoadingData ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3]?.map((i) => (
                  <CardSkeleton key={i} />
                ))}
              </div>
            ) : favorites?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {favorites?.map((trailer) =>
              <FavoriteTrailerCard key={trailer?.id} trailer={trailer} />
              )}
              </div> :

            <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
                <Icon name="Heart" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
                <p className="font-body text-muted-foreground mb-4">You haven't saved any favorites yet</p>
                <Button variant="default" onClick={() => navigate('/search-results')}>
                  Discover Trailers
                </Button>
              </div>
            }
          </section>

          {/* Rental History Section */}
          <section id="rental-history" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Clock" size={24} strokeWidth={2} className="text-primary" />
                <span>Rental History</span>
              </h2>
            </div>
            {isLoadingData ? (
              <div className="space-y-4">
                {[1, 2, 3]?.map((i) => (
                  <CardSkeleton key={i} variant="horizontal" />
                ))}
              </div>
            ) : rentalHistory?.length > 0 ?
            <div className="space-y-4">
                {rentalHistory?.map((rental) =>
              <RentalHistoryCard key={rental?.id} rental={rental} />
              )}
              </div> :

            <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
                <Icon name="Clock" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
                <p className="font-body text-muted-foreground mb-4">No rental history yet</p>
              </div>
            }
          </section>

          {/* Notifications Section */}
          <section id="notifications" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Bell" size={24} strokeWidth={2} className="text-primary" />
                <span>Notifications</span>
              </h2>
            </div>
            <div className="bg-card rounded-lg shadow-elevation-2 divide-y divide-border">
              {notifications?.map((notification) =>
              <div
                key={notification?.id}
                className={`p-4 flex items-start space-x-3 hover:bg-muted transition-smooth ${
                !notification?.read ? 'bg-primary/5' : ''}`
                }>
                
                  <div className="flex-shrink-0">
                    {notification?.type === 'booking' &&
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                        <Icon name="CheckCircle" size={20} className="text-success" strokeWidth={2} />
                      </div>
                  }
                    {notification?.type === 'reminder' &&
                  <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center">
                        <Icon name="Clock" size={20} className="text-warning" strokeWidth={2} />
                      </div>
                  }
                    {notification?.type === 'promotion' &&
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                        <Icon name="Tag" size={20} className="text-accent" strokeWidth={2} />
                      </div>
                  }
                  </div>
                  <div className="flex-1">
                    <p className="font-body text-foreground">{notification?.message}</p>
                    <p className="font-caption text-sm text-muted-foreground mt-1">{notification?.time}</p>
                  </div>
                  {!notification?.read &&
                <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                }
                </div>
              )}
            </div>
          </section>

          {/* Account Status Section */}
          <section className="mb-8">
            <div className="bg-card rounded-lg shadow-elevation-2 p-6">
              <h2 className="font-heading text-xl font-semibold text-foreground mb-4">Account Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} className="text-success" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-body font-medium text-foreground">Email Verified</p>
                    <p className="font-caption text-sm text-muted-foreground">Account active</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="Shield" size={24} className="text-success" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-body font-medium text-foreground">License Verified</p>
                    <p className="font-caption text-sm text-muted-foreground">Documents approved</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="CreditCard" size={24} className="text-success" strokeWidth={2} />
                  </div>
                  <div>
                    <p className="font-body font-medium text-foreground">Payment Method</p>
                    <p className="font-caption text-sm text-muted-foreground">Card on file</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      {/* MFA Setup Modal */}
      <MfaSetupModal
        isOpen={showMfaModal}
        onClose={() => setShowMfaModal(false)}
        onSuccess={handleMfaSetupSuccess}
        mfaEnabled={mfaEnabled}
        backupCodesCount={backupCodesCount}
      />
    </div>
  );

};

export default RenterDashboard;